import React from 'react';
import { StatusChip, Text } from '@fragments';
import { IconArrowRight, IconCheck, IconCircleCheck, IconFingerprint, IconLayoutList, IconQuote, IconRuler2, IconX } from '@tabler/icons-react';
import PersonalityWorks from '../PersonalityWorks';
import './styles.scss';

const number = (value, locale, digits = 0) => new Intl.NumberFormat(locale.replace('_', '-'), { maximumFractionDigits: digits }).format(value);

function Block({ icon: Icon, title, children }) {
  return <section className="studio-personality-view__block">
    <Text.subheading contrast={6} left={<Icon />}>{title}</Text.subheading>
    {children}
  </section>;
}

// What the mapping learned (`StudioPersonality`, contract 2.3.0), in the profile's language,
// in three parts. What stays: how the creator talks (summary, register, catchphrases, measured
// pace, language rules), which every script keeps. What changes: the strategy, the structure
// that goes viral set against what they do today, point by point (hook, retention, payoff,
// call to action, length). The structure is never copied from their videos. What works for
// them (2.5.0): their best and worst reels by reach compared, when the comparison ran.
// Metrics are measured by the backend, not estimated by the AI.
export default function PersonalityView({ studio: s, personality, mapped }) {
  const { t, locale } = s;
  const { metrics, strategy } = personality;
  const tags = [...personality.tone, t(`personalityFormality_${personality.formality}`), t(`personalityEnergy_${personality.energy}`)];
  return <div className="studio-personality-view">
    {mapped && <div className="studio-personality-view__done" role="status"><Text.span contrast={9} left={<IconCircleCheck />}>{t('personalityReady')}</Text.span></div>}
    <section className="studio-personality-view__lead">
      <Text.subheading contrast={6}>{t('personalityHowYouSpeak')}</Text.subheading>
      <Text.p contrast={8}>{personality.summary}</Text.p>
      <ul className="studio-personality-view__tags" aria-label={t('personalityTone')}>{tags.map(tag => <li key={tag}><Text.caption contrast={8}>{tag}</Text.caption></li>)}</ul>
      <Text.caption contrast={6}>{personality.stance}</Text.caption>
    </section>

    <section className="studio-personality-view__part" aria-labelledby="studio-personality-keeps">
      <div className="studio-personality-view__head">
        <Text.label id="studio-personality-keeps" contrast={9} left={<IconFingerprint />}>{t('personalityKeeps')}</Text.label>
        <Text.caption contrast={6}>{t('personalityKeepsHint')}</Text.caption>
      </div>
      <div className="studio-personality-view__grid">
        <Block icon={IconQuote} title={t('personalityPhrases')}>
          {personality.vocabulary.signature_phrases.length ? <ul className="studio-personality-view__list">{personality.vocabulary.signature_phrases.map(phrase => <li key={phrase.text}>
            <Text.span contrast={9}>“{phrase.text}”</Text.span><Text.caption contrast={6}>{phrase.usage}</Text.caption>
          </li>)}</ul> : <Text.caption contrast={6}>{t('personalityNoPhrases')}</Text.caption>}
        </Block>
        <Block icon={IconRuler2} title={t('personalityPace')}>
          <dl className="studio-personality-view__metrics">
            <div><dt><Text.caption contrast={6}>{t('personalityWordsPerMinute')}</Text.caption></dt><dd><Text.h5 weight={500} contrast={9}>{number(metrics.words_per_minute, locale)}</Text.h5></dd></div>
            <div><dt><Text.caption contrast={6}>{t('personalityWordsPerSentence')}</Text.caption></dt><dd><Text.h5 weight={500} contrast={9}>{number(metrics.words_per_sentence, locale, 1)}</Text.h5></dd></div>
            <div><dt><Text.caption contrast={6}>{t('personalityQuestions')}</Text.caption></dt><dd><Text.h5 weight={500} contrast={9}>{number(metrics.question_ratio * 100, locale)}%</Text.h5></dd></div>
            <div><dt><Text.caption contrast={6}>{t('personalityOralMarkers')}</Text.caption></dt><dd><Text.h5 weight={500} contrast={9}>{number(metrics.oral_markers_per_100_words, locale, 1)}</Text.h5></dd></div>
          </dl>
        </Block>
        <Block icon={IconCheck} title={t('personalityRules')}>
          <ul className="studio-personality-view__rules">
            {personality.rules.do.map(rule => <li key={`do-${rule}`}><Text.span contrast={8} left={<IconCheck />}>{rule}</Text.span></li>)}
            {personality.rules.dont.map(rule => <li key={`dont-${rule}`} className="is-dont"><Text.span contrast={7} left={<IconX />}>{rule}</Text.span></li>)}
          </ul>
        </Block>
      </div>
    </section>

    <section className="studio-personality-view__part" aria-labelledby="studio-personality-changes">
      <div className="studio-personality-view__head">
        <Text.label id="studio-personality-changes" contrast={9} left={<IconLayoutList />}>{t('personalityChanges')}</Text.label>
        <Text.caption contrast={6}>{t('personalityChangesHint')}</Text.caption>
      </div>
      <Text.p contrast={8}>{strategy.summary}</Text.p>
      <ol className="studio-personality-view__strategy">
        {strategy.items.map(item => {
          const keep = item.verdict === 'keep';
          const Chip = keep ? StatusChip.Success : StatusChip.Info;
          return <li key={item.area} className={`is-${item.verdict}`}>
            <div className="studio-personality-view__area">
              <Text.label contrast={9}>{t(`personalityArea_${item.area}`)}</Text.label>
              <Chip preserveCase text={t(`personalityVerdict_${item.verdict}`)} icon={keep ? <IconCheck /> : <IconArrowRight />} />
            </div>
            <div className="studio-personality-view__point">
              <Text.caption contrast={6}>{t('personalityToday')}</Text.caption>
              <Text.span contrast={7}>{item.today}</Text.span>
            </div>
            <div className="studio-personality-view__point">
              <Text.caption contrast={6}>{t('personalityInScripts')}</Text.caption>
              <Text.span contrast={9}>{item.direction}</Text.span>
            </div>
          </li>;
        })}
      </ol>
    </section>
    <PersonalityWorks studio={s} performance={personality.performance} />
    {personality.limitations && <Text.caption contrast={6}>{personality.limitations}</Text.caption>}
  </div>;
}
